/**
 * Billing Hooks
 *
 * React Query hooks for billing and wallet operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';
import { journeyKeys } from './useJourney';
import { businessKeys } from './useBusiness';
import { homeKeys } from './home-keys';
import {
  JourneyPricing,
  PricingDto,
  BillingService,
  CheckoutSession,
  CreateSponsorshipCheckoutInput,
  Invoice,
  InvoicePage,
  JourneyRenewalResult,
  JourneyUnlockResult,
  MySponsorshipOffer,
  Plan,
  PortalSession,
  ProJourneyUnlockResult,
  SponsoredTalent,
  SponsorshipCheckoutResult,
  SponsorshipOfferDetails,
  Subscription,
  ValidatedCouponResult,
  WalletBalance,
  WalletTransaction,
} from '../services/billing.service';

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const billingKeys = {
  all: ['billing'] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  plans: () => [...billingKeys.all, 'plans'] as const,
  journeyPricing: () => [...billingKeys.all, 'journeyPricing'] as const,
  wallet: () => [...billingKeys.all, 'wallet'] as const,
  walletTransactions: (params?: { type?: string }) =>
    [...billingKeys.all, 'walletTransactions', params] as const,
  /** Every page's prefix — invalidate this to refresh the history. */
  invoicesAll: () => [...billingKeys.all, 'invoices'] as const,
  invoices: (page: InvoicePage) => [...billingKeys.all, 'invoices', page] as const,
  sponsorships: () => [...billingKeys.all, 'sponsorships'] as const,
  sponsorshipOffer: (sponsorshipId: string) =>
    [...billingKeys.all, 'sponsorshipOffer', sponsorshipId] as const,
  /** The citizen's offers waiting for an answer; a prefix of `myOffersAll`. */
  myOffers: () => [...billingKeys.all, 'myOffers'] as const,
  myOffersAll: () => [...billingKeys.all, 'myOffers', 'all'] as const,
  sponsorshipRefundPreview: (sponsorshipId: string) =>
    [...billingKeys.all, 'sponsorshipRefundPreview', sponsorshipId] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// SUBSCRIPTION HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get current subscription
 */
export function useSubscription() {
  const client = useApiClient();
  const { isSignedIn, token } = useAuth();
  const service = new BillingService(client);

  return useQuery<Subscription>({
    queryKey: billingKeys.subscription(),
    queryFn: () => service.getSubscription(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Verify a completed Stripe Checkout Session and trigger server-side processing.
 * Returns a mutation that can be called with a sessionId.
 */
export function useVerifyCheckoutSession() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const service = new BillingService(client);

  return useMutation({
    mutationFn: (sessionId: string) => service.verifyCheckoutSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
    },
  });
}

/**
 * Get available plans
 */
export function usePlans() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useQuery<Plan[]>({
    queryKey: billingKeys.plans(),
    queryFn: () => service.getPlans(),
    staleTime: 1000 * 60 * 60, // Plans don't change often
  });
}

/**
 * Journey unlock/renewal pricing — server-owned, discount-decorated
 * (BL-022). Replaces the hardcoded €323.89/€359.88 dialog constants.
 */
export function useJourneyPricing() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useQuery<JourneyPricing>({
    queryKey: billingKeys.journeyPricing(),
    queryFn: () => service.getJourneyPricing(),
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Create checkout session
 */
export function useCreateCheckout() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useMutation<
    CheckoutSession,
    Error,
    { planId: string; successUrl: string; cancelUrl: string; couponCode?: string }
  >({
    mutationFn: (params) => service.createCheckout(params),
  });
}

/**
 * Validate an affiliate coupon code before checkout (BL-213). Returns a
 * mutation so the UI can validate on "Apply". The backend resolves the
 * discount; the FE never computes it.
 */
export function useValidateCoupon() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useMutation<ValidatedCouponResult, Error, { code: string; product?: string }>({
    mutationFn: ({ code, product }) => service.validateCoupon(code, product),
  });
}

/**
 * Cancel subscription
 */
export function useCancelSubscription() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => service.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// WALLET HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get wallet balance
 */
export function useWalletBalance() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useQuery<WalletBalance>({
    queryKey: billingKeys.wallet(),
    queryFn: () => service.getWalletBalance(),
  });
}

/**
 * Get wallet transactions
 */
export function useWalletTransactions(params?: {
  page?: number;
  pageSize?: number;
  type?: 'addition' | 'deduction';
}) {
  const client = useApiClient();
  const service = new BillingService(client);

  return useQuery<{ transactions: WalletTransaction[]; total: number }>({
    queryKey: billingKeys.walletTransactions(params),
    queryFn: () => service.getWalletTransactions(params),
  });
}

/**
 * Top up wallet
 */
export function useTopUpWallet() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; newBalance: number },
    Error,
    { amount: number; paymentMethodId: string }
  >({
    mutationFn: (params) => service.topUpWallet(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.wallet() });
      queryClient.invalidateQueries({ queryKey: billingKeys.walletTransactions() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// INVOICE HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * One page of the invoice history. The page is in the query key — it used to
 * be left out, so every page read the first twenty rows.
 */
export function useInvoices(page: InvoicePage) {
  const client = useApiClient();
  const service = new BillingService(client);

  return useQuery<{ invoices: Invoice[]; total: number }>({
    queryKey: billingKeys.invoices(page),
    queryFn: () => service.getInvoices(page),
    placeholderData: (previous) => previous,
  });
}

/**
 * Open an invoice's document: asks the API for a link Stripe mints now.
 * Callers open a window synchronously on the click (a pop-up blocker allows
 * that) and point it at the URL when it arrives.
 */
export function useOpenInvoiceDocument() {
  const client = useApiClient();
  const service = new BillingService(client);
  return useMutation({
    mutationFn: (invoiceId: string) => service.getInvoiceDocument(invoiceId),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY UNLOCK & RENEWAL HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Create a Stripe Checkout session to unlock a journey instance (one-time payment).
 * On success, the caller should redirect to the returned URL.
 */
export function useUnlockJourney() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useMutation<
    JourneyUnlockResult,
    Error,
    {
      journeyId: string;
      successUrl: string;
      cancelUrl: string;
      couponCode?: string;
    }
  >({
    mutationFn: (params) => service.unlockJourney(params),
    // No cache invalidation here — the page will reload after Stripe redirect
    // and the checkout.session.completed webhook will update the DB.
  });
}

/**
 * Instantly unlock a journey for Pro subscribers (no Stripe payment).
 * On success, invalidates billing and journey caches.
 */
export function useProUnlockJourney() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<ProJourneyUnlockResult, Error, string>({
    mutationFn: (journeyId) => service.unlockJourneyPro(journeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
    },
  });
}

/**
 * Create a Stripe Checkout session to renew a journey instance (one-time payment).
 * On success, the caller should redirect to the returned URL.
 */
export function useRenewJourney() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useMutation<
    JourneyRenewalResult,
    Error,
    {
      journeyId: string;
      successUrl: string;
      cancelUrl: string;
      couponCode?: string;
    }
  >({
    mutationFn: (params) => service.renewJourney(params),
    // No cache invalidation here — the page will reload after Stripe redirect
    // and the checkout.session.completed webhook will update the DB.
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PORTAL + SPONSORSHIP HOOKS (B2B)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Mint a Stripe Customer Portal session URL. The caller is responsible
 * for opening the URL (usually via `window.open(..., '_blank')`).
 */
export function useCreatePortalSession() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useMutation<PortalSession, Error, string>({
    // The argument is the returnUrl — where Stripe sends the user on exit.
    mutationFn: (returnUrl: string) => service.createPortalSession(returnUrl),
  });
}

/**
 * List active/pending/cancelling talent sponsorships for the current tenant.
 */
export function useSponsorships() {
  const client = useApiClient();
  const { isSignedIn, token } = useAuth();
  const service = new BillingService(client);

  return useQuery<SponsoredTalent[]>({
    queryKey: billingKeys.sponsorships(),
    queryFn: () => service.getSponsorships(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Create a Stripe Checkout session to sponsor a talent. On success, the
 * caller should redirect the browser to `result.url`. No cache invalidation
 * is performed here because the page will unload on redirect; the webhook
 * plus verify-session fallback will refresh data when the user returns.
 */
export function useCreateSponsorshipCheckout() {
  const client = useApiClient();
  const service = new BillingService(client);

  return useMutation<SponsorshipCheckoutResult, Error, CreateSponsorshipCheckoutInput>({
    mutationFn: (input) => service.createSponsorshipCheckout(input),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// SPONSORSHIP LIFECYCLE (2026-Q2 redesign)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Admin: dispatch the activation email to the talent. Used by
 * SponsorReturnPage step 3 in business-web.
 */
export function useDispatchSponsorshipActivation() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<
    { sendId: string; alreadyDispatched?: boolean },
    Error,
    { sponsorshipId: string; customMessage?: string; force?: boolean }
  >({
    mutationFn: ({ sponsorshipId, customMessage, force }) =>
      service.dispatchSponsorshipActivation(sponsorshipId, { customMessage, force }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.sponsorships() });
    },
  });
}

/**
 * Admin: preview the pro-rated refund amount for an active sponsorship.
 * Read-only — used by the Cancel-Sponsorship confirmation dialog.
 */
export function useSponsorshipRefundPreview(sponsorshipId: string | null) {
  const client = useApiClient();
  const { isSignedIn, token } = useAuth();
  const service = new BillingService(client);

  return useQuery({
    queryKey: billingKeys.sponsorshipRefundPreview(sponsorshipId ?? ''),
    queryFn: () => service.getSponsorshipRefundPreview(sponsorshipId!),
    enabled: isSignedIn && !!token && !!sponsorshipId,
  });
}

/**
 * Admin: cancel an active sponsorship. Pro-rated refund.
 */
export function useCancelSponsorship() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<BillingService['cancelSponsorship']>>,
    Error,
    { sponsorshipId: string; reason?: string }
  >({
    mutationFn: ({ sponsorshipId, reason }) => service.cancelSponsorship(sponsorshipId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.sponsorships() });
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.all });
      // The kanban / pipeline view subscribes to `businessKeys.talents`
      // (NOT journeyKeys), so the talent card stays pinned to the
      // Sponsored bucket without this invalidation. Cascade-invalidate
      // the entire `business` namespace — talents lists, vetting
      // overview, and pipeline stats all read off the same backend
      // tables this mutation just mutated.
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
}

/**
 * Admin: abandon a pending sponsorship before talent acceptance.
 * Full refund.
 */
export function useAbandonSponsorship() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<BillingService['abandonSponsorship']>>,
    Error,
    { sponsorshipId: string; reason?: string }
  >({
    mutationFn: ({ sponsorshipId, reason }) =>
      service.abandonSponsorship(sponsorshipId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.sponsorships() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.all });
      // Kanban refresh — see comment in `useCancelSponsorship`.
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
}

/**
 * Citizen: load a sponsorship offer the citizen has been emailed about.
 * Mounted on the dashboard + on the email-link landing page.
 */
export function useSponsorshipOffer(sponsorshipId: string | null) {
  const client = useApiClient();
  const { isSignedIn, token } = useAuth();
  const service = new BillingService(client);

  return useQuery<SponsorshipOfferDetails>({
    queryKey: billingKeys.sponsorshipOffer(sponsorshipId ?? ''),
    queryFn: () => service.getSponsorshipOffer(sponsorshipId!),
    enabled: isSignedIn && !!token && !!sponsorshipId,
  });
}

/**
 * Citizen: the sponsorship offers waiting for my answer, NOT put off — Home's
 * prompt. The twin of `useMyPendingInvitations`.
 */
export function useMyPendingSponsorshipOffers(enabled = true) {
  const client = useApiClient();
  const { isSignedIn, token } = useAuth();
  const service = new BillingService(client);

  return useQuery({
    queryKey: billingKeys.myOffers(),
    queryFn: () => service.mySponsorshipOffers(),
    enabled: enabled && isSignedIn && !!token,
    // An offer can be abandoned by the company between visits.
    staleTime: 30_000,
  });
}

/**
 * Citizen: every sponsorship offer waiting for my answer, the ones put off
 * with "Not now" included, each with its `deferredUntil` — listed beside the
 * invitations (the bell's Invitations tab, the phone's Inbox). The twin of
 * `useMyInvitations`.
 */
export function useMySponsorshipOffers(enabled = true) {
  const client = useApiClient();
  const { isSignedIn, token } = useAuth();
  const service = new BillingService(client);

  return useQuery({
    queryKey: billingKeys.myOffersAll(),
    queryFn: () => service.mySponsorshipOffers({ includeDeferred: true }),
    enabled: enabled && isSignedIn && !!token,
    staleTime: 30_000,
  });
}

/** Citizen: "Not now" on an offer. Not an answer; it comes back when the window lapses. */
export function useDeferSponsorshipOffer() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<void, Error, { sponsorshipId: string }>({
    mutationFn: ({ sponsorshipId }) => service.deferSponsorshipOffer(sponsorshipId),
    onSuccess: (_data, { sponsorshipId }) => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.myOffers() });
      void queryClient.invalidateQueries({ queryKey: billingKeys.sponsorshipOffer(sponsorshipId) });
      // Home lists the offer among what needs the citizen.
      void queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/**
 * Citizen: accept a sponsorship offer. Flips the journey to sponsored_b2b.
 */
export function useAcceptSponsorshipOffer() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<BillingService['acceptSponsorshipOffer']>>,
    Error,
    { sponsorshipId: string }
  >({
    mutationFn: ({ sponsorshipId }) => service.acceptSponsorshipOffer(sponsorshipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all });
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      // Acceptance flips pipeline_stage → 'mobility'; the business
      // kanban needs to refetch to show the talent in the Sponsored
      // bucket (the admin sees this immediately on the same screen).
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
      // Home's "Needs an answer" card for the offer.
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/**
 * Citizen: decline a sponsorship offer. Triggers automatic full refund.
 */
export function useDeclineSponsorshipOffer() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<BillingService['declineSponsorshipOffer']>>,
    Error,
    { sponsorshipId: string; reason?: string }
  >({
    mutationFn: ({ sponsorshipId, reason }) =>
      service.declineSponsorshipOffer(sponsorshipId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      queryClient.invalidateQueries({ queryKey: journeyKeys.all });
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/**
 * Toggle family-access on a sponsorship.
 */
export function useToggleSponsorshipFamilyAccess() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; familyAccess: boolean },
    Error,
    { sponsorshipId: string; enabled: boolean }
  >({
    mutationFn: ({ sponsorshipId, enabled }) =>
      service.toggleSponsorshipFamilyAccess(sponsorshipId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.sponsorships() });
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
    },
  });
}

/**
 * Cancel family add-on subscription
 */
export function useCancelFamilyAddon() {
  const client = useApiClient();
  const service = new BillingService(client);
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; effectiveDate: string }, Error, void>({
    mutationFn: () => service.cancelFamilyAddon(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
    },
  });
}

// Re-export types
export type {
  CheckoutSession,
  JourneyPricing,
  PricingDto,
  CreateSponsorshipCheckoutInput,
  Invoice,
  InvoicePage,
  JourneyRenewalResult,
  JourneyUnlockResult,
  MySponsorshipOffer,
  Plan,
  PortalSession,
  ProJourneyUnlockResult,
  SponsoredTalent,
  SponsorshipCheckoutResult,
  SponsorshipOfferDetails,
  Subscription,
  ValidatedCouponResult,
  WalletBalance,
  WalletTransaction,
};
